import { InsertableStripe, Stripe } from '@/types'

export const {
  appName: APP_NAME = 'Vessel Sites',
  apiHost: API_HOST = null,
  appHost: APP_HOST = null,
  authHost: AUTH_HOST = API_HOST || null,
  orgId: ORG_ID
} = window.VSSL_ENV || {}

/**
 * Implementation of debounce:
 * https://www.joshwcomeau.com/snippets/javascript/debounce/
 */
export function debounce(callback: Function, wait: number) {
  let timeoutId: number
  return (...args: unknown[]) => {
    window.clearTimeout(timeoutId)
    timeoutId = window.setTimeout(() => {
      callback.apply(null, args)
    }, wait)
  }
}

export function getImageUrl(url: string, style = '') {
  style = style ? style + '/' : ''
  return API_HOST + '/images/' + style + url
}

export function clone<T>(value: T): T {
  if (Array.isArray(value)) {
    return [...value].map(clone) as T
  } else if (value && typeof value === 'object') {
    const entries = Object.entries({ ...value }).map(([key, val]) => [
      key,
      clone(val)
    ])
    return Object.fromEntries(entries)
  } else {
    return value
  }
}

/** Generate a random alphanumeric value to use as an id */
export function generateId(length = 9) {
  const alphanumeric =
    '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
  let result = ''
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * alphanumeric.length)
    result += alphanumeric.charAt(randomIndex)
  }
  return result
}

/** Creates a new insertable stripe from an insertable configuration. */
export function createInsertable(insert: InsertableStripe) {
  const apply = {
    id: generateId(),
    sticky: null,
    insertable: null
  }
  return removeNull(
    Object.assign(clone(insert), apply, typeDefaults(insert.type))
  ) as Stripe
}

/** Pre-configure sticky elements */
export function addStickyDefaults(sticky: Stripe): Stripe {
  const apply = {
    id: generateId(),
    insertable: null
  }
  return sticky.id || !sticky.sticky
    ? sticky
    : (removeNull(
        Object.assign(clone(sticky), apply, typeDefaults(sticky.type))
      ) as Stripe)
}

/** Accepts an object and removes all null or undefined values. */
export function removeNull(obj: Record<string, unknown>) {
  const culled: typeof obj = {}
  Object.keys(obj).forEach((key) => {
    if (obj[key] !== undefined && obj[key] !== null) {
      culled[key] = obj[key]
    }
  })
  return culled
}

/** Takes a list of stripes and re-weights them, respecting 'stickies' as well. */
export function reweight(
  list: Stripe[] = [],
  stickyWeights: Record<string, number | undefined> = {}
) {
  const stripes = clone(list)
  const stickies = stripes
    .filter((stripe) => stripe.sticky)
    .map((stripe) =>
      Object.assign(stripe, { weight: stickyWeights[stripe.type || ''] })
    )
  return [stickies, stripes.filter((stripe) => !stripe.sticky)]
    .map((grouping) => {
      return grouping.sort((a, b) =>
        (a?.weight || 0) > (b?.weight || 0) ? 1 : -1
      )
    })
    .reduce((joined, group) => {
      return [...joined, ...group]
    }, [])
    .map((stripe, idx) => {
      return Object.assign(stripe, { weight: idx })
    })
}

/** Takes a list of sticky_stripes and return a key-value grouping of weights. */
export function stickyWeights(
  stickyStripes: Stripe[] = []
): Record<string, number | undefined> {
  return stickyStripes.reduce(
    (weights: Record<string, number | undefined>, stripe) => {
      return { ...weights, [stripe.type || '']: stripe.weight }
    },
    {}
  )
}

export function getServiceNameFromUrl(url = '') {
  const [, name] =
    url.match(
      /([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)\.(?:com|app|dev|io|so|me)/
    ) || []

  return name
}

export function getUrlFromEmbed(embed = '') {
  const [, url] =
    embed.match(
      // Regex to match on an iframe's src attribute
      /<iframe[^>]*src=["']([^"']+)["'][^>]*>/
    ) || []

  return url
}

function getQualtricsEmbedFromUrl(url: string) {
  const [, organization, formId] =
    url.match(
      /^https:\/\/([a-zA-Z0-9-\.]+)\.qualtrics\.com\/jfe\/form\/SV_([a-zA-Z0-9]+)$/
    ) || []

  if (!organization || !formId) return

  const iframeSrc =
    `//${organization}.qualtrics.com/jfe/form/SV_${formId}?` +
    new URLSearchParams({
      Q_CHL: 'iframe'
    })

  return `
    <iframe
      src="${iframeSrc}"
      frameborder="0"
    ></iframe>
  `
}

function getVimeoEmbedFromUrl(url: string) {
  /**
   * Matches the vid (136522833) and potential hash key (9a912fe706) in these
   * formats:
   * https://vimeo.com/136522833/9a912fe706
   * https://player.vimeo.com/video/136522833/9a912fe706
   * //player.vimeo.com/video/136522833/9a912fe706
   * //player.vimeo.com/video/136522833?foo=bar
   */
  const [, videoId, hash] =
    url.match(
      /^(?:http[s]?:|)\/\/[w]{0,3}.?(?:.+\.|)vimeo\.com\/(?:[^\d]+\/|)(\d+)(?:\/([0-9a-f]+))?/
    ) || []

  if (!videoId) return

  const iframeUrlParams = new URLSearchParams({
    title: '0',
    byline: '0',
    portrait: '0',
    color: 'ffffff'
  })

  // The h param was either found in the regex match, or we can try to parse it
  // out of the url's search params
  const hParam = hash || new URLSearchParams(url.split('?')[1] || '').get('h')
  if (hParam) iframeUrlParams.set('h', hParam)

  const iframeSrc = `//player.vimeo.com/video/${videoId}?${iframeUrlParams}`

  return `
    <iframe
      src="${iframeSrc}"
      frameborder="0"
      webkitallowfullscreen
      mozallowfullscreen
      allowfullscreen
      allow="autoplay; fullscreen"
    ></iframe>
  `
}

function getYouTubeEmbedFromUrl(url: string) {
  const [, videoId] =
    url.match(
      /^http[s]?:\/\/[w]{0,3}.?youtube.com\/(?:.*?.*v=|embed\/)([a-zA-Z-0-9_]*).*$/
    ) || []

  if (!videoId) return

  const iframeSrc =
    `//www.youtube.com/embed/${videoId}?` +
    new URLSearchParams({
      color: 'white',
      rel: '0',
      showinfo: '0'
    })

  return `
    <iframe
      src="${iframeSrc}"
      type="text/html"
      frameborder="0"
      allow="autoplay; fullscreen"
    ></iframe>
  `
}

export function formEmbed(url: string) {
  const serviceName = getServiceNameFromUrl(url)
  if (serviceName === 'qualtrics') {
    return getQualtricsEmbedFromUrl(url)
  }
}

/** Helps with embedding videos */
export function videoEmbed(url = '') {
  const serviceName = getServiceNameFromUrl(url)
  if (serviceName === 'vimeo') {
    return getVimeoEmbedFromUrl(url)
  } else if (serviceName === 'youtube') {
    return getYouTubeEmbedFromUrl(url)
  }
}

/**
 * Default values to apply to insertable components (by type).
 *
 * @param  {string} type type of component
 * @return {Object}
 */
function typeDefaults(type?: string) {
  const defaults = {
    'stripe-gallery': {
      slides: [{ id: generateId(), weight: 0 }]
    },
    'stripe-googlemap': {
      maptype: 'roadmap'
    },
    'stripe-grid': {
      items: [
        { id: generateId(), weight: 0 },
        { id: generateId(), weight: 1 },
        { id: generateId(), weight: 2 }
      ]
    },
    'stripe-link-grid': {
      links: [
        { id: generateId(), weight: 0 },
        { id: generateId(), weight: 1 },
        { id: generateId(), weight: 2 },
        { id: generateId(), weight: 3 },
        { id: generateId(), weight: 4 },
        { id: generateId(), weight: 5 }
      ]
    },
    'stripe-text-image': {
      layout: ''
    },
    'stripe-related': {
      links: [
        { id: generateId(), weight: 0 },
        { id: generateId(), weight: 1 },
        { id: generateId(), weight: 2 }
      ]
    },
    'stripe-toc': {
      title: { html: 'Page Contents' }
    },
    'stripe-table': {
      columnCount: 3,
      rowCount: 3,
      maxColumns: 12,
      hasHeadersInFirstRow: true
    }
  }
  return defaults[type as keyof typeof defaults] || {}
}
